import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { Preferences } from '@capacitor/preferences';
import { TextToSpeech } from '@capacitor-community/text-to-speech';
import { useSocketStore } from './socket';
import { apiFetch } from '../services/api';
import { isCapacitor, getRuntimeConfig } from '../config/runtime';
import {
  backfillPanelDeviceConfiguration,
  isPanelDeviceTakeover,
  readStoredPanelCredential,
  writePanelDeviceMode,
  type PanelDeviceMode,
} from '../config/panelDevice';
import { isPanelSurfacePath } from '../services/apiCredential';
import type { PanelCommandPayload } from './panelMediaPlayer';
import { TAKEOVER_SCOPE_ID, registerPanelScope, unregisterPanelScope } from '../services/panelScopes';
import { setVolume as setAudioVolume, playAudioUrl } from '../services/audioPlayback';
import { startRinging, stopRinging } from '../utils/ringTone';
import { localeTag } from '../utils/formatDate';
import { startLivePlayback, addPCMChunk, stopLivePlayback } from '../services/liveAudioPlayback';

// ─── Types ────────────────────────────────────────────────────────────────────

export type TouchPanelHardwareType = 'TABLET' | 'NSPANEL' | 'CUSTOM';
export type TouchPanelStatus = 'ONLINE' | 'OFFLINE' | 'SLEEPING';
export type VoiceState = 'idle' | 'listening' | 'processing' | 'speaking';
export type MediaState = 'idle' | 'playing' | 'paused' | 'buffering';
export type CallState = 'idle' | 'incoming' | 'active' | 'ended';

export interface TouchPanelPage {
  id: string;
  panelId: string;
  name: string;
  pageType: string;
  icon: string;
  sortOrder: number;
  config: TouchPanelPageConfig;
  gridConfig: { columns: number; rows: number; gap: number };
  enabled: boolean;
  widgets?: TouchPanelPageWidget[];
  createdAt: string;
  updatedAt: string;
}

export interface TouchPanelPageWidget {
  id: string;
  pageId: string;
  widgetType: string;
  widgetConfig: TouchPanelWidgetConfig;
  col: number;
  row: number;
  colSpan: number;
  rowSpan: number;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * A change to one grid widget, as the panel editor's inspector emits it: the
 * editor owns the widget objects, the palette only names the change (writing
 * into the `selectedWidget` prop is the mutation the lint flags).
 */
export type WidgetPatch = { id: string; patch: Partial<TouchPanelPageWidget> };

export interface TouchPanel {
  id: string;
  name: string;
  hardwareType: TouchPanelHardwareType;
  status: TouchPanelStatus;
  roomId: string | null;
  deviceId: string | null;
  integrationId: string | null;
  macAddress: string | null;
  ipAddress: string | null;
  appVersion: string | null;
  firmwareVersion: string | null;
  screenWidth: number | null;
  screenHeight: number | null;
  lastSeen: string | null;
  adminPin: string | null;
  screensaverTimeout: number;
  brightnessIdle: number;
  brightnessActive: number;
  volume: number;
  voiceEnabled: boolean;
  mediaPlayerEnabled: boolean;
  isPaired: boolean;
  deviceMode?: 'play' | 'touch';
  config: PanelConfigBag;
  createdAt: string;
  updatedAt: string;
  pages?: TouchPanelPage[];
}

export interface SensorData {
  proximity?: boolean;
  ambient_light?: number;
  battery?: number;
  screen_state?: boolean;
  screen_brightness?: number;
  presence?: boolean;
  temperature?: number;
  humidity?: number;
  volume?: number;
  mute?: boolean;
  disk_used_gb?: number;
  disk_free_gb?: number;
}

export interface MediaPlayback {
  state: MediaState;
  itemId?: string;
  url?: string;
  title?: string;
  artist?: string;
  album?: string;
  artUrl?: string;
  mediaType?: 'audio' | 'video' | string;
  position?: number;
  duration?: number;
}

export interface IncomingCall {
  fromPanelId: string;
  fromPanelName: string;
  /** The caller's WebRTC offer, answered with `acceptCall`. */
  offer?: RTCSessionDescriptionInit | null;
  bridgeCallId?: string;
}

/** Security runtime state pushed to this panel via touch:command. */
export interface SecurityPanelState {
  activeModeId: string | null;
  alarmActive: boolean;
  alarmTriggeredAt: string | null;
  armingCountdown: number | null;
  status: string | null;
}

// ─── Persistence helpers ──────────────────────────────────────────────────────

const PREFS_PANEL_MODE = 'touch_panel_mode';
const PREFS_PANEL_ID = 'touch_panel_id';
const PREFS_ROOM_ID = 'touch_panel_room_id';
const PREFS_ADMIN_PIN = 'touch_panel_admin_pin';
const PREFS_DEVICE_TOKEN = 'touch_device_token';
const PREFS_DEVICE_MODE = 'otto_device_mode';

async function savePreference(key: string, value: string | null) {
  if (isCapacitor()) {
    if (value === null) {
      await Preferences.remove({ key });
    } else {
      await Preferences.set({ key, value });
    }
  }
  // Always mirror to localStorage so isTouchDevice() can detect paired state
  if (value === null) {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(key, value);
  }
}

async function loadPreference(key: string): Promise<string | null> {
  if (isCapacitor()) {
    const { value } = await Preferences.get({ key });
    return value;
  }
  return localStorage.getItem(key);
}

function redactTokenizedUrl(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  return value.replace(/([?&]token=)[^&]*/gi, '$1[redacted]');
}

function mediaCommandPayloadMeta(payload?: PanelCommandPayload): Record<string, unknown> | null {
  if (!payload || typeof payload !== 'object') return null;
  return {
    itemId: payload.itemId ?? payload.id,
    title: payload.title,
    mediaType: payload.mediaType,
    mimeType: payload.mimeType ?? payload.contentType,
    hasUrl: Boolean(payload.url),
    url: redactTokenizedUrl(payload.url),
    hasUri: Boolean(payload.uri),
    uri: redactTokenizedUrl(payload.uri),
    hasArtUrl: Boolean(payload.artUrl ?? payload.thumbnail ?? payload.thumbnailUrl),
    startPosition: payload.startPosition,
  };
}

/** The panel's own config bag: the keys the panel surfaces read and write. */
export interface PanelConfigBag {
  themeMode?: string;
  themeColor?: string;
  themeCustomHue?: number;
  autoBrightness?: boolean;
  brightnessIdle?: number;
  brightnessActive?: number;
  screensaverTimeout?: number;
  screensaverSource?: string;
  screensaverSlideInterval?: number;
  securitySirenMode?: string;
  muted?: boolean;
  [key: string]: unknown;
}

/** A page's config bag — its keys depend on the page type. */
export interface TouchPanelPageConfig {
  [key: string]: unknown;
}

/** A widget's config bag — its keys depend on the widget type. */
export interface TouchPanelWidgetConfig {
  [key: string]: unknown;
}

/** The panel row `/auth/pair-device` answers with. */
export interface PairedPanelRow {
  id: string;
  roomId?: string | null;
  adminPin?: string | null;
  pages?: TouchPanelPage[];
  [key: string]: unknown;
}

/** The identity the panel registers itself with. */
export interface PanelRegistrationInfo {
  hardwareType?: string;
  roomId?: string | null;
  panelId?: string;
  connected?: boolean;
  [key: string]: unknown;
}

const DEFAULT_APK_URL = 'https://storage.torik.ai/otto-firmware/otto-latest.apk';

// ─── Store ────────────────────────────────────────────────────────────────────

export const useTouchPanelStore = defineStore('touchPanel', () => {
  const socketStore = useSocketStore();

  // ── Panel-mode state ──────────────────────────────────────────────────────
  const isPanelMode = ref(false);
  const panelId = ref<string | null>(null);
  const roomId = ref<string | null>(null);
  const adminPin = ref<string | null>(null);
  const panelStatus = ref<TouchPanelStatus>('OFFLINE');
  const pages = ref<TouchPanelPage[]>([]);
  const panelConfig = ref<PanelConfigBag>({});
  const registrationError = ref<string | null>(null);

  // ── Device token (replaces user auth for panel devices) ───────────────────
  const deviceToken = ref<string | null>(null);

  // ── The takeover panel scope of this window ───────────────────────────────
  //
  // This store holds the WALL PANEL's state: the device that IS a panel, where
  // the panel surface is the window. It is not "the panel" of the process — a
  // panel surface can also be mounted as a miniapp beside a normal session
  // (`stores/panelApp.ts`), with its own credential, its own pages and its own
  // role. The two scopes never share a credential, so nothing here may assume it
  // is the only panel context in the process: each scope registers itself in the
  // registry (`services/panelScopes.ts`) and the API layer asks the registry
  // whose request it is, rather than reading ambience off this store.
  const effectivePanelCredential = () => deviceToken.value ?? readStoredPanelCredential();

  function syncPanelScope(): void {
    registerPanelScope({
      id: TAKEOVER_SCOPE_ID,
      mode: 'takeover',
      credential: effectivePanelCredential(),
      panelId: panelId.value,
      role: 'panel',
    });
  }

  // ── Device mode (touch vs play) ───────────────────────────────────────────
  const deviceMode = ref<'touch' | 'play' | null>(null);

  // ── Voice state ───────────────────────────────────────────────────────────
  const voiceState = ref<VoiceState>('idle');
  const voiceTranscript = ref<string>('');
  const voiceResponse = ref<string>('');

  // ── Media playback state ──────────────────────────────────────────────────
  const mediaPlayback = ref<MediaPlayback>({ state: 'idle' });

  // ── Panel capability flags (synced from server) ──────────────────────────
  const voiceEnabled = ref(false);
  const volume = ref(70);
  const muted = ref(false);
  const mediaPlayerEnabled = ref(false);

  // ── Security state (pushed when this panel is enrolled as an alarm
  //    control panel: touch:command security:state / security:alarm) ─────────
  const securityState = ref<SecurityPanelState>({
    activeModeId: null,
    alarmActive: false,
    alarmTriggeredAt: null,
    armingCountdown: null,
    status: null,
  });
  /** Whether the panel security screen is opened manually (settings sheet). */
  const securityScreenOpen = ref(false);

  // ── Announcement overlay state ─────────────────────────────────────────────
  const announcement = ref<{ text: string; priority: string } | null>(null);
  const debugMessage = ref<string | null>(null);
  let _announceTimer: ReturnType<typeof setTimeout> | null = null;
  let _debugTimer: ReturnType<typeof setTimeout> | null = null;

  function dismissAnnouncement() {
    announcement.value = null;
    if (_announceTimer) {
      clearTimeout(_announceTimer);
      _announceTimer = null;
    }
    // Stop Web Speech API
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }

  function showDebug(msg: string) {
    debugMessage.value = msg;
    if (_debugTimer) clearTimeout(_debugTimer);
    _debugTimer = setTimeout(() => {
      debugMessage.value = null;
    }, 5000);
  }

  // ── Inter-panel call state ────────────────────────────────────────────────
  const callState = ref<CallState>('idle');
  const incomingCall = ref<IncomingCall | null>(null);
  const activeCallPeerId = ref<string | null>(null);

  // ── Admin panel list ──────────────────────────────────────────────────────
  const panels = ref<TouchPanel[]>([]);
  const loading = ref(false);
  const initialized = ref(false);

  // ── Derived ───────────────────────────────────────────────────────────────
  const activePages = computed(() => pages.value.filter((p) => p.enabled).sort((a, b) => a.sortOrder - b.sortOrder));
  const isRegistered = computed(() => !!panelId.value && panelStatus.value !== 'OFFLINE');
  const isPaired = computed(() => !!deviceToken.value);
  const isPlayMode = computed(() => deviceMode.value === 'play' && isPaired.value);

  // ── Socket listeners ──────────────────────────────────────────────────────
  // Named handler so setupSocketListeners() can re-register its own connect
  // listener without removing the SocketStore's 'connect' handler — that one
  // flips `socketStore.connected` to true. Using `socket.off('connect')`
  // (no listener) removes ALL connect listeners including the store's, so the
  // panel stays permanently marked "Disconnected" even though the socket is up.
  let _registerOnConnect: (() => void) | null = null;

  function setupSocketListeners() {
    const socket = socketStore.socket;
    console.log('[TouchPanel] setupSocketListeners called, socket:', !!socket);
    if (!socket) return;

    // Remove old listeners to avoid duplicates
    if (_registerOnConnect) socket.off('connect', _registerOnConnect);
    socket.off('touch:registered');
    socket.off('touch:config:updated');
    socket.off('touch:command');
    socket.off('touch:panel:status');
    socket.off('touch:voice:wake_word');
    socket.off('touch:voice:state');
    socket.off('touch:call:answer');

    // Re-register on every reconnect (handles server restarts)
    _registerOnConnect = () => {
      console.log('[TouchPanel] Socket reconnected, re-registering with server');
      registerWithServer();
    };
    socket.on('connect', _registerOnConnect);

    socket.on('touch:registered', (data: { panel: TouchPanel }) => {
      console.log('[TouchPanel] 🎉 touch:registered received', data.panel.id);
      panelId.value = data.panel.id;
      panelStatus.value = 'ONLINE';
      panelConfig.value = data.panel.config || {};
      voiceEnabled.value = data.panel.voiceEnabled ?? false;
      volume.value = data.panel.volume ?? 70;
      mediaPlayerEnabled.value = data.panel.mediaPlayerEnabled ?? false;
      muted.value = Boolean(data.panel.config?.muted);
      setAudioVolume(muted.value ? 0 : (data.panel.volume ?? 70) / 100);
      registrationError.value = null;
      if (data.panel.pages) {
        pages.value = data.panel.pages;
      }
      // Always apply server-authoritative adminPin (clears old PIN when null)
      adminPin.value = data.panel.adminPin ?? null;
      savePreference(PREFS_ADMIN_PIN, data.panel.adminPin ?? null);
      savePreference(PREFS_PANEL_ID, data.panel.id);
    });

    socket.on(
      'touch:config:updated',
      (data: {
        pages?: TouchPanelPage[];
        config?: PanelConfigBag;
        roomId?: string | null;
        adminPin?: string | null;
        screensaverTimeout?: number;
      }) => {
        console.log('[TouchPanel] touch:config:updated received');
        if (data.pages) pages.value = data.pages;
        if (data.config) panelConfig.value = { ...panelConfig.value, ...data.config };
        if ('roomId' in data) {
          roomId.value = data.roomId ?? null;
          if (data.roomId) savePreference(PREFS_ROOM_ID, data.roomId);
          else savePreference(PREFS_ROOM_ID, null);
        }
        if ('adminPin' in data) {
          adminPin.value = data.adminPin ?? null;
          savePreference(PREFS_ADMIN_PIN, data.adminPin ?? null);
        }
        if ('screensaverTimeout' in data) {
          panelConfig.value = { ...panelConfig.value, screensaverTimeout: data.screensaverTimeout };
        }
        if ('voiceEnabled' in data && data.voiceEnabled !== undefined)
          voiceEnabled.value = data.voiceEnabled as boolean;
        if ('volume' in data && data.volume !== undefined) {
          volume.value = data.volume as number;
          setAudioVolume(muted.value ? 0 : (data.volume as number) / 100);
        }
        if ('mediaPlayerEnabled' in data && data.mediaPlayerEnabled !== undefined)
          mediaPlayerEnabled.value = data.mediaPlayerEnabled as boolean;
        if (data.config && 'muted' in data.config) {
          muted.value = Boolean(data.config.muted);
          setAudioVolume(muted.value ? 0 : volume.value / 100);
        }
      },
    );

    socket.on('touch:command', (data: { command: string; payload?: PanelCommandPayload }) => {
      handleCommand(data.command, data.payload);
    });

    socket.on('touch:panel:status', (data: { panelId: string; status: TouchPanelStatus }) => {
      if (data.panelId === panelId.value) {
        panelStatus.value = data.status;
      }
    });

    // Voice events
    socket.on('touch:voice:wake_word', () => {
      voiceState.value = 'listening';
    });

    socket.on('touch:voice:state', (data: { state: VoiceState; transcript?: string; response?: string }) => {
      voiceState.value = data.state;
      if (data.transcript !== undefined) voiceTranscript.value = data.transcript;
      if (data.response !== undefined) voiceResponse.value = data.response;
    });

    // Incoming inter-panel call
    socket.on('touch:call:answer', () => {
      // This event is handled by PanelCallOverlay directly via socket
    });
  }

  // ── Command handler ───────────────────────────────────────────────────────
  function handleCommand(command: string, payload?: PanelCommandPayload) {
    if (command.startsWith('media:')) {
      console.info('[TouchPanel] Media command received', {
        command,
        payload: mediaCommandPayloadMeta(payload),
        panelId: panelId.value,
        hasDeviceToken: Boolean(deviceToken.value || localStorage.getItem('touch_device_token')),
        socketConnected: Boolean(socketStore.socket?.connected),
      });
    }

    const navigatePath = payload?.path;
    if (command === 'navigate' && navigatePath) {
      import('../router').then(({ default: router }) => {
        router.push(navigatePath);
      });
    } else if (command === 'reload') {
      // Navigate to root before reloading so Capacitor's WebView doesn't
      // try to re-fetch a deep URL that only exists in the SPA router.
      window.location.replace(window.location.origin + '/');
    } else if (command === 'brightness' && payload?.value !== undefined) {
      const brightnessPercent = payload.value;
      console.log('[TouchPanel] Brightness command:', brightnessPercent);
      import('@capacitor-community/screen-brightness')
        .then(({ ScreenBrightness }) => {
          ScreenBrightness.setBrightness({ brightness: Math.max(0, Math.min(1, brightnessPercent / 100)) });
        })
        .catch(() => {});
    } else if (command === 'call:incoming' && payload) {
      callState.value = 'incoming';
      incomingCall.value = {
        fromPanelId: payload.fromPanelId ?? '',
        fromPanelName: payload.fromPanelName ?? '',
        offer: payload.offer ?? null,
        bridgeCallId: payload.bridgeCallId,
      };
      startRinging();
    } else if (command === 'call:end') {
      stopRinging();
      callState.value = 'ended';
      setTimeout(() => {
        callState.value = 'idle';
        incomingCall.value = null;
        activeCallPeerId.value = null;
      }, 2000);
    } else if (command === 'announce:broadcast:start') {
      startLivePlayback();
    } else if (command === 'announce:broadcast:chunk' && payload?.data) {
      // payload.data may arrive as a plain object with byte values — convert to ArrayBuffer
      let buf: ArrayBuffer;
      if (payload.data instanceof ArrayBuffer) {
        buf = payload.data;
      } else if (payload.data instanceof Uint8Array) {
        // `Uint8Array.buffer` is typed `ArrayBufferLike`; these are plain buffers.
        buf = payload.data.buffer as ArrayBuffer;
      } else {
        // Socket.IO binary objects arrive as Buffer-like objects
        const byteValues = typeof payload.data === 'object' && payload.data !== null ? Object.values(payload.data) : [];
        buf = new Uint8Array(byteValues).buffer as ArrayBuffer;
      }
      addPCMChunk(buf);
    } else if (command === 'announce:broadcast:stop') {
      stopLivePlayback();
    } else if (command === 'announce' && payload) {
      // Show text overlay (muted panels get a text-only announcement)
      if (payload.text) {
        dismissAnnouncement();
        announcement.value = { text: payload.text, priority: payload.priority ?? 'normal' };
        _announceTimer = setTimeout(() => {
          announcement.value = null;
        }, 10000);

        // Muted panels skip all announcement audio
        if (muted.value) return;

        const announceVol = payload.volume ?? volume.value;
        if (payload.audioUrl) {
          // Server synthesised audio via Piper — play the WAV file
          TextToSpeech.stop().catch(() => {});
          // Resolve relative URLs to absolute using the hub server origin (needed for Capacitor native fetch)
          let audioUrl = payload.audioUrl;
          if (audioUrl.startsWith('/')) {
            const cfg = getRuntimeConfig();
            const origin = cfg.apiUrl.replace(/\/api$/, '');
            audioUrl = `${origin}${audioUrl}`;
          }
          setAudioVolume(announceVol / 100);
          playAudioUrl(audioUrl, payload.priority ?? 'normal')
            .finally(() => setAudioVolume(volume.value / 100))
            .catch(() => undefined);
        } else if (payload.text) {
          // Piper unavailable — fall back to device TTS. The tag follows the
          // app's language (`localeTag`, `src/utils/formatDate.ts`), not a
          // literal: `payload.text` is the hub's copy for this panel's locale,
          // so pinning English spoke a Slovene sentence with an English voice.
          // A device without a voice for the tag gets the closest installed one
          // (the platform's own fallback) rather than failing.
          TextToSpeech.stop().catch(() => {});
          TextToSpeech.speak({
            text: payload.text,
            lang: localeTag(),
            rate: 1.0,
            pitch: 1.0,
            volume: announceVol / 100,
          }).catch(() => {
            // Web Speech API fallback (browser context) — same rule here: an
            // utterance with no `lang` follows the *runtime*, which is the
            // opposite defect on a machine whose language is not the app's.
            if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
              window.speechSynthesis.cancel();
              const utter = new SpeechSynthesisUtterance(payload.text);
              utter.lang = localeTag();
              utter.volume = announceVol / 100;
              window.speechSynthesis.speak(utter);
            }
          });
        }
      }
    } else if (command === 'media:play' && payload) {
      import('./panelMediaPlayer').then(({ usePanelMediaPlayerStore }) => {
        usePanelMediaPlayerStore().playRemotePayload(payload);
      });
    } else if (command === 'media:stop') {
      import('./panelMediaPlayer').then(({ usePanelMediaPlayerStore }) => {
        usePanelMediaPlayerStore().stop();
      });
    } else if (command === 'media:pause') {
      import('./panelMediaPlayer').then(({ usePanelMediaPlayerStore }) => {
        usePanelMediaPlayerStore().pause();
      });
    } else if (command === 'media:resume') {
      import('./panelMediaPlayer').then(({ usePanelMediaPlayerStore }) => {
        usePanelMediaPlayerStore().resume();
      });
    } else if (command === 'media:set_volume' && payload?.level !== undefined) {
      const level = Math.max(0, Math.min(100, Number(payload.level)));
      volume.value = level;
      setAudioVolume(level / 100);
      import('./panelMediaPlayer').then(({ usePanelMediaPlayerStore }) => {
        usePanelMediaPlayerStore().setVolume(level);
      });
    } else if (command === 'media:next') {
      import('./panelMediaPlayer').then(({ usePanelMediaPlayerStore }) => {
        usePanelMediaPlayerStore().next();
      });
    } else if (command === 'media:previous') {
      import('./panelMediaPlayer').then(({ usePanelMediaPlayerStore }) => {
        usePanelMediaPlayerStore().previous();
      });
    } else if (command === 'media:set_repeat') {
      import('./panelMediaPlayer').then(({ usePanelMediaPlayerStore }) => {
        const panelPlayer = usePanelMediaPlayerStore();
        if (payload?.mode === 'one' || payload?.mode === 'all' || payload?.mode === 'none') {
          panelPlayer.setRepeatMode(payload.mode);
        }
      });
    } else if (command === 'media:set_mute') {
      const isMuted = Boolean(payload?.muted);
      muted.value = isMuted;
      setAudioVolume(isMuted ? 0 : volume.value / 100);
    } else if (command === 'security:state') {
      securityState.value = {
        activeModeId: payload?.activeModeId ?? securityState.value.activeModeId ?? null,
        alarmActive: Boolean(payload?.alarmActive),
        alarmTriggeredAt: payload?.alarmTriggeredAt ?? securityState.value.alarmTriggeredAt ?? null,
        armingCountdown: typeof payload?.armingCountdown === 'number' ? payload.armingCountdown : null,
        status: payload?.status ?? null,
      };
    } else if (command === 'security:alarm') {
      securityState.value = {
        ...securityState.value,
        alarmActive: true,
        alarmTriggeredAt: payload?.alarmTriggeredAt ?? securityState.value.alarmTriggeredAt ?? new Date().toISOString(),
      };
    } else if (command === 'update') {
      installApkUpdate(`remote-command:${command}`).catch(() => {});
    }
  }

  async function installApkUpdate(source = 'manual') {
    if (!isCapacitor()) {
      console.warn('[TouchPanel] APK update ignored outside Capacitor', { source });
      return;
    }

    showDebug('Preparing update...');
    let apkUrl = DEFAULT_APK_URL;
    try {
      const { url } = await apiFetch<{ url: string | null }>('/touch-panels/apk-update-url');
      if (url) apkUrl = url;
    } catch (__caught: unknown) {
      const err = __caught as Error;
      console.warn('[TouchPanel] Failed to fetch configured APK URL; using default', {
        source,
        error: err?.message ?? String(err),
      });
    }

    try {
      console.info('[TouchPanel] APK update starting', {
        source,
        url: redactTokenizedUrl(apkUrl),
        hasDeviceToken: Boolean(deviceToken.value || localStorage.getItem('touch_device_token')),
      });

      const { Filesystem, Directory } = await import('@capacitor/filesystem');
      showDebug('Downloading APK...');
      await Filesystem.downloadFile({
        url: apkUrl,
        path: 'otto-update.apk',
        directory: Directory.Cache,
      });

      const { uri } = await Filesystem.getUri({
        path: 'otto-update.apk',
        directory: Directory.Cache,
      });

      const { FileOpener } = await import('@capacitor-community/file-opener');
      showDebug('Installing update...');
      await FileOpener.open({
        filePath: uri,
        contentType: 'application/vnd.android.package-archive',
        openWithDefault: true,
      });

      console.info('[TouchPanel] APK update install intent opened', { source });
    } catch (__caught: unknown) {
      const err = __caught as Error;
      const message = err?.message ?? 'unknown error';
      console.error('[TouchPanel] APK update failed', { source, error: message });
      showDebug(`Update failed: ${message}`);
      throw err;
    }
  }

  // ── Device pairing (replaces login) ───────────────────────────────────────

  /**
   * Pair this device using a PIN. Called from PanelPairing.vue.
   * Communicates directly with /api/auth/pair-device using the hub API URL.
   */
  async function pairWithPin(pin: string): Promise<void> {
    const result = await apiFetch<{ panelId: string; deviceToken: string; panel: PairedPanelRow }>(
      '/auth/pair-device',
      {
        method: 'POST',
        body: JSON.stringify({ pin }),
      },
    );
    await storePairingResult(result);
    // Disconnect any stale socket so enablePanelMode reconnects with the new token
    socketStore.disconnect();
    await enablePanelMode({ roomId: result.panel.roomId ?? undefined });
  }

  /**
   * Pair this device using a scanned QR token.
   */
  async function pairWithQRToken(qrToken: string): Promise<void> {
    const result = await apiFetch<{ panelId: string; deviceToken: string; panel: PairedPanelRow }>(
      '/auth/pair-device',
      {
        method: 'POST',
        body: JSON.stringify({ qrToken }),
      },
    );
    await storePairingResult(result);
    // Disconnect any stale socket so enablePanelMode reconnects with the new token
    socketStore.disconnect();
    await enablePanelMode({ roomId: result.panel.roomId ?? undefined });
  }

  async function storePairingResult(result: { panelId: string; deviceToken: string; panel: PairedPanelRow }) {
    deviceToken.value = result.deviceToken;
    panelId.value = result.panelId;

    await Promise.all([
      savePreference(PREFS_DEVICE_TOKEN, result.deviceToken),
      savePreference(PREFS_PANEL_ID, result.panelId),
    ]);

    // The scope follows the credential that was just paired, so the panel's own
    // requests stop depending on when `restoreState()` last ran.
    syncPanelScope();
  }

  async function unpair() {
    deviceToken.value = null;
    panelId.value = null;
    isPanelMode.value = false;
    deviceMode.value = null;
    unregisterPanelScope(TAKEOVER_SCOPE_ID);

    await Promise.all([
      savePreference(PREFS_DEVICE_TOKEN, null),
      savePreference(PREFS_PANEL_ID, null),
      savePreference(PREFS_PANEL_MODE, null),
      savePreference(PREFS_DEVICE_MODE, null),
    ]);
  }

  async function setDeviceMode(mode: 'touch' | 'play' | null) {
    deviceMode.value = mode;
    await savePreference(PREFS_DEVICE_MODE, mode);
  }

  // ── Panel mode actions ────────────────────────────────────────────────────
  async function enablePanelMode(opts: { roomId?: string; pin?: string } = {}) {
    console.log('[TouchPanel] enablePanelMode called', opts);
    console.log(
      '[TouchPanel] Current deviceToken:',
      deviceToken.value ? `${deviceToken.value.substring(0, 20)}...` : 'NONE',
    );
    isPanelMode.value = true;
    if (opts.roomId) roomId.value = opts.roomId;
    if (opts.pin !== undefined) adminPin.value = opts.pin;

    await Promise.all([
      savePreference(PREFS_PANEL_MODE, 'true'),
      opts.roomId ? savePreference(PREFS_ROOM_ID, opts.roomId) : Promise.resolve(),
      opts.pin !== undefined ? savePreference(PREFS_ADMIN_PIN, opts.pin) : Promise.resolve(),
    ]);

    // This window IS the panel from here on: its credential answers for it.
    syncPanelScope();

    // Ensure socket is connected for panel communication
    console.log(
      '[TouchPanel] Socket connected?',
      socketStore.socket?.connected,
      'socket exists?',
      !!socketStore.socket,
    );
    if (!socketStore.socket?.connected) {
      console.log(
        '[TouchPanel] Calling socketStore.connect() with deviceToken:',
        deviceToken.value ? `${deviceToken.value.substring(0, 20)}...` : 'NONE',
      );
      socketStore.connect(deviceToken.value || undefined);
    }

    setupSocketListeners();

    if (socketStore.socket?.connected) {
      console.log('[TouchPanel] Socket connected, registering immediately');
      await registerWithServer();
    } else {
      console.log('[TouchPanel] Socket not connected, will register via connect listener');
    }
  }

  async function disablePanelMode() {
    isPanelMode.value = false;
    panelStatus.value = 'OFFLINE';
    // The window stops being a panel: its credential leaves the registry with it
    // (a miniapp scope, if one is mounted, is a different scope and is untouched).
    unregisterPanelScope(TAKEOVER_SCOPE_ID);

    const socket = socketStore.socket;
    if (socket && panelId.value) {
      socket.emit('touch:disconnect', { panelId: panelId.value });
    }

    await Promise.all([savePreference(PREFS_PANEL_MODE, null), savePreference(PREFS_PANEL_ID, null)]);

    panelId.value = null;
  }

  async function registerWithServer() {
    const socket = socketStore.socket;
    console.log('[TouchPanel] registerWithServer called, socket:', !!socket, 'connected:', socket?.connected);

    if (!socket || !socket.connected) {
      registrationError.value = 'Not connected to server';
      console.log('[TouchPanel] Registration failed: not connected');
      return;
    }

    registrationError.value = null;

    const info: PanelRegistrationInfo = {
      hardwareType: 'TABLET',
      roomId: roomId.value,
    };

    info.connected = true;

    if (panelId.value) {
      info.panelId = panelId.value;
    }

    console.log('[TouchPanel] Emitting touch:register', info);
    socket.emit('touch:register', info);
  }

  function updateSensorData(data: SensorData) {
    const socket = socketStore.socket;
    if (!socket || !panelId.value) return;
    socket.emit('touch:sensor', { panelId: panelId.value, ...data });
  }

  function sendHeartbeat() {
    const socket = socketStore.socket;
    if (!socket || !panelId.value) return;
    socket.emit('touch:heartbeat', { panelId: panelId.value });
  }

  function reportSleeping(isSleeping: boolean) {
    const socket = socketStore.socket;
    if (!socket || !panelId.value) return;
    socket.emit('touch:sleeping', { panelId: panelId.value, sleeping: isSleeping });
    panelStatus.value = isSleeping ? 'SLEEPING' : 'ONLINE';
  }

  /** Report current media playback state back to server */
  function reportMediaState(state: MediaPlayback) {
    const socket = socketStore.socket;
    mediaPlayback.value = state;
    if (!socket || !panelId.value) return;
    socket.emit('touch:media:state', { panelId: panelId.value, ...state });
  }

  function reportMediaError(error: Record<string, unknown>) {
    const socket = socketStore.socket;
    const payload = {
      panelId: panelId.value,
      ...error,
      hasDeviceToken: Boolean(deviceToken.value || localStorage.getItem('touch_device_token')),
    };
    console.warn('[TouchPanel] Reporting media error', payload);
    if (!socket || !panelId.value) return;
    socket.emit('touch:media:error', payload);
  }

  function reportMediaDiagnostic(event: Record<string, unknown>) {
    const socket = socketStore.socket;
    const payload = {
      panelId: panelId.value,
      ...event,
      hasDeviceToken: Boolean(deviceToken.value || localStorage.getItem('touch_device_token')),
    };
    console.info('[TouchPanel] Reporting media diagnostic', payload);
    if (!socket || !panelId.value) return;
    socket.emit('touch:media:diagnostic', payload);
  }

  /** Accept an incoming inter-panel call */
  function acceptCall(answer: RTCSessionDescriptionInit | null) {
    const socket = socketStore.socket;
    if (!socket || !incomingCall.value) return;
    socket.emit('touch:call:answer', {
      toPanelId: incomingCall.value.fromPanelId,
      answer,
      bridgeCallId: incomingCall.value.bridgeCallId,
    });
    callState.value = 'active';
    activeCallPeerId.value = incomingCall.value.fromPanelId;
  }

  /** Reject or end a call */
  function endCall() {
    stopRinging();
    const socket = socketStore.socket;
    const peerId = activeCallPeerId.value ?? incomingCall.value?.fromPanelId;
    const bridgeId = incomingCall.value?.bridgeCallId;
    if (socket && (peerId || bridgeId)) {
      socket.emit('touch:call:end', { toPanelId: peerId, bridgeCallId: bridgeId });
    }
    callState.value = 'idle';
    incomingCall.value = null;
    activeCallPeerId.value = null;
  }

  // ── Admin REST actions ────────────────────────────────────────────────────
  async function loadPanels() {
    loading.value = true;
    try {
      panels.value = await apiFetch<TouchPanel[]>('/touch-panels');
    } finally {
      loading.value = false;
    }
  }

  async function loadPanel(id: string): Promise<TouchPanel> {
    return apiFetch<TouchPanel>(`/touch-panels/${id}`);
  }

  async function createPanel(data: Partial<TouchPanel>): Promise<TouchPanel> {
    const panel = await apiFetch<TouchPanel>('/touch-panels', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    panels.value.push(panel);
    return panel;
  }

  async function updatePanel(id: string, data: Partial<TouchPanel>): Promise<TouchPanel> {
    const panel = await apiFetch<TouchPanel>(`/touch-panels/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    const idx = panels.value.findIndex((p) => p.id === id);
    if (idx !== -1) panels.value[idx] = panel;
    return panel;
  }

  async function deletePanel(id: string) {
    await apiFetch(`/touch-panels/${id}`, { method: 'DELETE' });
    panels.value = panels.value.filter((p) => p.id !== id);
  }

  async function loadPanelPages(id: string): Promise<TouchPanelPage[]> {
    return apiFetch<TouchPanelPage[]>(`/touch-panels/${id}/pages`);
  }

  async function savePanelPages(id: string, pagesData: TouchPanelPage[]): Promise<TouchPanelPage[]> {
    return apiFetch<TouchPanelPage[]>(`/touch-panels/${id}/pages`, {
      method: 'PUT',
      body: JSON.stringify({ pages: pagesData }),
    });
  }

  async function sendCommand(id: string, command: string, payload?: PanelCommandPayload) {
    await apiFetch(`/touch-panels/${id}/command`, {
      method: 'POST',
      body: JSON.stringify({ command, payload }),
    });
  }

  /** Admin: generate a pairing PIN for a new panel */
  async function generatePairingPin(
    name: string,
    roomId?: string,
    hardwareType?: string,
  ): Promise<{ panelId: string; pin: string; expiresAt: string }> {
    return apiFetch('/auth/panel/generate-pin', {
      method: 'POST',
      body: JSON.stringify({ name, roomId, hardwareType }),
    });
  }

  /** Admin: generate a QR payload for a new panel */
  async function generateQRPayload(
    name: string,
    roomId?: string,
    hubUrl?: string,
    hardwareType?: string,
  ): Promise<{ panelId: string; qrPayload: string; expiresAt: string }> {
    return apiFetch('/auth/panel/generate-qr', {
      method: 'POST',
      body: JSON.stringify({ name, roomId, hubUrl, hardwareType }),
    });
  }

  /** Admin: regenerate a pairing PIN for an existing panel */
  async function regeneratePairingPin(panelId: string): Promise<{ pin: string; expiresAt: string }> {
    return apiFetch(`/auth/panel/${panelId}/regenerate-pin`, { method: 'POST' });
  }

  /** Admin: unpair a panel */
  async function unpairPanel(id: string) {
    await apiFetch(`/auth/panel/${id}`, { method: 'DELETE' });
    await loadPanels();
  }

  // ── Persistence restore on startup ───────────────────────────────────────
  async function restoreState() {
    console.log('[TouchPanel] Restoring state from preferences...');
    const [mode, storedPanelId, storedRoomId, storedPin, storedToken, storedDeviceMode] = await Promise.all([
      loadPreference(PREFS_PANEL_MODE),
      loadPreference(PREFS_PANEL_ID),
      loadPreference(PREFS_ROOM_ID),
      loadPreference(PREFS_ADMIN_PIN),
      loadPreference(PREFS_DEVICE_TOKEN),
      loadPreference(PREFS_DEVICE_MODE),
    ]);

    console.log('[TouchPanel] Restored:', {
      mode,
      panelId: storedPanelId,
      hasToken: !!storedToken,
      deviceMode: storedDeviceMode,
    });

    if (storedToken) {
      deviceToken.value = storedToken;
      console.log('[TouchPanel] Device token loaded from storage');
    }

    if (storedDeviceMode === 'touch' || storedDeviceMode === 'play') {
      deviceMode.value = storedDeviceMode;
    }

    // Only re-enter panel mode when this device IS a panel:
    //
    //   * the owner's explicit configuration (`config/panelDevice.ts`) — the gate
    //     that replaced `isTouchDevice()`, which asked about hardware instead of
    //     about what this device was configured to be; or
    //   * a `/panel`/`/play` route, which is a panel surface by construction.
    //
    // On an app, a stale `touch_panel_mode=true` (interrupted pairing) must not
    // flip the whole window into panel routing and panel credential selection.
    const deviceIsPanel = isPanelDeviceTakeover();
    if (deviceIsPanel) {
      // A wall panel that predates the explicit configuration adopts it here, so
      // the legacy hardware rule stops being what keeps it working.
      backfillPanelDeviceConfiguration();
    }
    const isPanelContext = deviceIsPanel || isPanelSurfacePath(window.location.pathname);

    if (mode === 'true' && isPanelContext) {
      console.log('[TouchPanel] Panel mode enabled, connecting socket...');
      isPanelMode.value = true;
      panelId.value = storedPanelId;
      roomId.value = storedRoomId;
      adminPin.value = storedPin;

      // The window's panel scope, so the API layer knows whose credential
      // answers for the requests the panel surface makes.
      syncPanelScope();

      // Connect socket for panel device
      if (!socketStore.socket?.connected) {
        console.log('[TouchPanel] Socket not connected, calling connect()');
        socketStore.connect(storedToken || undefined);
      }

      setupSocketListeners();

      if (socketStore.socket?.connected) {
        console.log('[TouchPanel] Socket connected on restore, registering');
        await registerWithServer();
      } else {
        console.log('[TouchPanel] Socket not connected, watching for connection');
        const unwatch = socketStore.$subscribe(() => {
          if (socketStore.socket?.connected) {
            console.log('[TouchPanel] Socket connected (watched), registering');
            unwatch();
            setupSocketListeners();
            registerWithServer();
          }
        });
      }
    }

    initialized.value = true;
  }

  // ── PIN verification ──────────────────────────────────────────────────────
  function verifyAdminPin(pin: string): boolean {
    if (!adminPin.value) return true;
    return pin === adminPin.value;
  }

  /**
   * Record what the owner wants this device to be — the explicit configuration
   * that replaced the hardware check (`config/panelDevice.ts`):
   *
   *   * `takeover` — this device is a panel; the panel surface is the window
   *     (a wall panel, unchanged);
   *   * `app` — the app stays an app and a panel surface is opened as a miniapp;
   *   * `null` — forget the configuration, which leaves the safe default (`app`).
   *
   * Turning takeover OFF leaves panel mode; turning it ON enters it, but only
   * with a credential to be a panel with — an unpaired device pairs first.
   */
  async function configurePanelDevice(mode: PanelDeviceMode | null) {
    writePanelDeviceMode(mode);
    if (mode === 'takeover') {
      if (effectivePanelCredential()) await enablePanelMode();
      return;
    }
    if (isPanelMode.value) await disablePanelMode();
  }

  async function setLocalExitPin(pin: string) {
    adminPin.value = pin;
    await savePreference(PREFS_ADMIN_PIN, pin);
  }

  /** Apply + persist mute state (panel settings sheet / admin). */
  async function setMuted(value: boolean) {
    muted.value = value;
    setAudioVolume(value ? 0 : volume.value / 100);
    if (!panelId.value) return;
    try {
      await apiFetch(`/touch-panels/${panelId.value}/mute`, {
        method: 'POST',
        body: JSON.stringify({ muted: value }),
      });
    } catch (__caught: unknown) {
      const err = __caught as Error;
      console.warn('[TouchPanel] Failed to persist mute state', err?.message ?? err);
    }
  }

  /** Apply + persist volume (panel settings sheet). */
  async function setVolume(value: number) {
    const clamped = Math.max(0, Math.min(100, Math.round(value)));
    volume.value = clamped;
    setAudioVolume(muted.value ? 0 : clamped / 100);
    if (!panelId.value) return;
    try {
      await apiFetch(`/touch-panels/${panelId.value}`, {
        method: 'PUT',
        body: JSON.stringify({ volume: clamped }),
      });
    } catch (__caught: unknown) {
      const err = __caught as Error;
      console.warn('[TouchPanel] Failed to persist volume', err?.message ?? err);
    }
  }

  // ── Page config helper ───────────────────────────────────────────────────
  function getPageConfig(pageType: string): TouchPanelPageConfig {
    const page = pages.value.find((p) => p.pageType === pageType);
    return page?.config ?? {};
  }

  // ── Widget CRUD (Admin) ──────────────────────────────────────────────────

  async function loadPageWidgets(panelId: string, pageId: string): Promise<TouchPanelPageWidget[]> {
    return apiFetch<TouchPanelPageWidget[]>(`/touch-panels/${panelId}/pages/${pageId}/widgets`);
  }

  async function createPageWidget(
    panelId: string,
    pageId: string,
    data: Partial<TouchPanelPageWidget>,
  ): Promise<TouchPanelPageWidget> {
    return apiFetch<TouchPanelPageWidget>(`/touch-panels/${panelId}/pages/${pageId}/widgets`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async function updatePageWidget(
    panelId: string,
    pageId: string,
    widgetId: string,
    data: Partial<TouchPanelPageWidget>,
  ): Promise<TouchPanelPageWidget> {
    return apiFetch<TouchPanelPageWidget>(`/touch-panels/${panelId}/pages/${pageId}/widgets/${widgetId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async function deletePageWidget(panelId: string, pageId: string, widgetId: string): Promise<void> {
    await apiFetch(`/touch-panels/${panelId}/pages/${pageId}/widgets/${widgetId}`, { method: 'DELETE' });
  }

  async function bulkUpdatePageWidgets(
    panelId: string,
    pageId: string,
    widgets: Array<Partial<TouchPanelPageWidget> & { id: string }>,
  ): Promise<TouchPanelPageWidget[]> {
    return apiFetch<TouchPanelPageWidget[]>(`/touch-panels/${panelId}/pages/${pageId}/widgets/bulk`, {
      method: 'PUT',
      body: JSON.stringify({ widgets }),
    });
  }

  async function autoBuildPage(panelId: string, pageId: string, roomId?: string): Promise<TouchPanelPageWidget[]> {
    return apiFetch<TouchPanelPageWidget[]>(`/touch-panels/${panelId}/pages/${pageId}/auto-build`, {
      method: 'POST',
      body: JSON.stringify({ roomId }),
    });
  }

  return {
    // State
    isPanelMode,
    panelId,
    roomId,
    adminPin,
    panelStatus,
    pages,
    panelConfig,
    registrationError,
    deviceToken,
    voiceState,
    voiceTranscript,
    voiceResponse,
    voiceEnabled,
    volume,
    muted,
    mediaPlayerEnabled,
    mediaPlayback,
    callState,
    incomingCall,
    activeCallPeerId,
    panels,
    loading,
    initialized,
    // Computed
    activePages,
    isRegistered,
    isPaired,
    isPlayMode,
    deviceMode,
    // Pairing
    setDeviceMode,
    pairWithPin,
    pairWithQRToken,
    unpair,
    generatePairingPin,
    generateQRPayload,
    regeneratePairingPin,
    unpairPanel,
    // Panel-mode actions
    enablePanelMode,
    disablePanelMode,
    registerWithServer,
    installApkUpdate,
    updateSensorData,
    sendHeartbeat,
    reportSleeping,
    reportMediaState,
    reportMediaError,
    reportMediaDiagnostic,
    acceptCall,
    endCall,
    setupSocketListeners,
    verifyAdminPin,
    configurePanelDevice,
    setLocalExitPin,
    getPageConfig,
    restoreState,
    // Admin REST
    loadPanels,
    loadPanel,
    createPanel,
    updatePanel,
    deletePanel,
    loadPanelPages,
    savePanelPages,
    sendCommand,
    // Widget CRUD
    loadPageWidgets,
    createPageWidget,
    updatePageWidget,
    deletePageWidget,
    bulkUpdatePageWidgets,
    autoBuildPage,
    // Announcement
    announcement,
    dismissAnnouncement,
    debugMessage,
    showDebug,
    // Security (panel alarm screen)
    securityState,
    securityScreenOpen,
    setMuted,
    setVolume,
  };
});
